import React from 'react';
import type { MealBreakConfig, RateMultiplier } from '@/types';
import { CreditExtraTimeTimeline, RateSplitCallout, PartialIntervalCallout } from '@/components/config_forms/shared/help-graphics/CreditExtraTimeTimeline';

interface CreditExtraTimeTimelineInjectableParams {
  mealBreakConfig: MealBreakConfig;
  rateMultipliers: RateMultiplier[];
  onMealConfigChange?: (updates: Partial<MealBreakConfig>) => void;
}

export function buildCreditExtraTimeTimelineInjectable({
  mealBreakConfig,
  rateMultipliers,
  onMealConfigChange,
}: CreditExtraTimeTimelineInjectableParams): {
  injectables: Record<string, React.ReactNode>;
} {
  const ladder = rateMultipliers
    .filter(m => m.defaultMultiplier != null)
    .sort((a, b) => (a.defaultMultiplier ?? 0) - (b.defaultMultiplier ?? 0))
    .map(m => ({ code: m.code, name: m.name, multiplier: m.defaultMultiplier! }));

  const timeCreditMinutes = mealBreakConfig.timeCreditMinutes ?? 10;
  const creditIntervalMinutes = mealBreakConfig.creditIntervalMinutes ?? 30;

  const element = React.createElement(CreditExtraTimeTimeline, {
    creditIntervalMinutes,
    timeCreditMinutes,
    deadlineHours: mealBreakConfig.mealDeadlineHours ?? 5,
    recurringDeadline: mealBreakConfig.recurringMealDeadline ?? false,
    premiumBasis: mealBreakConfig.premiumBasis ?? 'STRAIGHT_TIME',
    mealTriggerMode: mealBreakConfig.mealTriggerMode ?? 'deadline',
    requiredBreaks: mealBreakConfig.requiredBreaks ?? 2,
    ladder,
    ...(onMealConfigChange ? {
      onCreditIntervalChange: (value: number | null) => onMealConfigChange({ creditIntervalMinutes: value ?? 30 }),
      onTimeCreditChange: (value: number | null) => onMealConfigChange({ timeCreditMinutes: value ?? 10 }),
      onPremiumBasisChange: (basis: string) => onMealConfigChange({ premiumBasis: basis }),
    } : {}),
  });

  const rateSplitElement = React.createElement(RateSplitCallout, {
    creditLabel: `+${timeCreditMinutes}m`,
    ladder,
    timeCreditMinutes,
    creditIntervalMinutes,
  });

  const partialIntervalElement = React.createElement(PartialIntervalCallout, {
    creditLabel: `+${timeCreditMinutes}m`,
    creditIntervalMinutes,
  });

  return {
    injectables: {
      credit_extra_time_timeline: element,
      credit_extra_time_rate_split: rateSplitElement,
      credit_extra_time_partial: partialIntervalElement,
    },
  };
}
